import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Autocomplete,
  TextField,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Chip,
  Stack
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';

// Add this constant
import { SERVER_BASE_URL } from '../config';

const API_BASE_URL = SERVER_BASE_URL;

// Define the structure for a voice object (based on ElevenLabs API)
interface Voice {
  voice_id: string;
  name: string;
  // Add other relevant fields if needed, e.g., category, labels
}

interface VoiceSettings {
  voiceId: string;
  stability: number;
  similarityBoost: number;
  style: number;
  speed: number;
}

interface SettingsDialogProps {
  open: boolean;
  onClose: () => void;
  currentSettings: VoiceSettings;
  onSave: (newSettings: VoiceSettings) => void;
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onClose,
  currentSettings,
  onSave,
}) => {
  const [voices, setVoices] = useState<Voice[]>([]);
  const [loadingVoices, setLoadingVoices] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Local state for editing settings within the dialog
  const [selectedVoiceId, setSelectedVoiceId] = useState<string>(currentSettings.voiceId);
  const [stability, setStability] = useState<number>(currentSettings.stability);
  const [similarityBoost, setSimilarityBoost] = useState<number>(currentSettings.similarityBoost);
  const [style, setStyle] = useState<number>(currentSettings.style ?? 0);
  const [speed, setSpeed] = useState<number>(currentSettings.speed ?? 1.0);

  // Preview state
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Add voice state
  const [addVoiceId, setAddVoiceId] = useState<string>('');
  const [addingVoice, setAddingVoice] = useState<boolean>(false);
  const [addVoiceSuccess, setAddVoiceSuccess] = useState<string | null>(null);

  const PREVIEW_TEXT = "This is the Billboard Top 10 Chart Reveal!";

  const fetchVoices = async () => {
    setLoadingVoices(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/voices`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.voices && Array.isArray(data.voices)) {
        setVoices(data.voices);
        // Ensure the current voice ID is still valid, otherwise reset to default/first?
        if (!data.voices.some((v: Voice) => v.voice_id === currentSettings.voiceId)) {
          console.warn('Current voice ID not found in fetched voices, resetting...');
          // Optionally reset to the first voice or a default
          if (data.voices.length > 0) {
             setSelectedVoiceId(data.voices[0].voice_id);
          }
        }
      } else {
        throw new Error('Invalid voice data format received');
      }
    } catch (err) {
      console.error('Failed to fetch voices:', err);
      setError(err instanceof Error ? err.message : 'Failed to load voices');
    } finally {
      setLoadingVoices(false);
    }
  };

  // Fetch voices when the dialog opens
  useEffect(() => {
    if (open) {
      fetchVoices();
    }
  }, [open, currentSettings.voiceId]); // Re-fetch if dialog opens or initial voiceId changes

  // Update local state when currentSettings prop changes (e.g., saved externally)
  useEffect(() => {
    setSelectedVoiceId(currentSettings.voiceId);
    setStability(currentSettings.stability);
    setSimilarityBoost(currentSettings.similarityBoost);
    setStyle(currentSettings.style ?? 0);
    setSpeed(currentSettings.speed ?? 1.0);
  }, [currentSettings]);

  // Cleanup audio on unmount or dialog close
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Stop audio when dialog closes
  useEffect(() => {
    if (!open && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
    }
  }, [open]);

  const handleAddVoice = async () => {
    const trimmedId = addVoiceId.trim();
    if (!trimmedId) return;

    setAddingVoice(true);
    setError(null);
    setAddVoiceSuccess(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/voices/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceId: trimmedId }),
      });
      const data = await response.json();

      if (response.status === 409) {
        setError(`"${data.voice?.name || trimmedId}" is already in the approved list`);
        return;
      }
      if (!response.ok) {
        throw new Error(data.error || 'Failed to add voice');
      }

      setAddVoiceSuccess(`Added "${data.voice.name}"`);
      setAddVoiceId('');
      // Refresh the voice list and select the newly added voice
      await fetchVoices();
      setSelectedVoiceId(data.voice.voice_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add voice');
    } finally {
      setAddingVoice(false);
    }
  };

  const handleRemoveVoice = async (voiceId: string, voiceName: string) => {
    setError(null);
    setAddVoiceSuccess(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/voices/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voiceId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to remove voice');
      setAddVoiceSuccess(`Removed "${voiceName || voiceId}"`);
      // If the removed voice was selected, fall back to the first remaining one.
      if (selectedVoiceId === voiceId) {
        const remaining = voices.filter(v => v.voice_id !== voiceId);
        setSelectedVoiceId(remaining[0]?.voice_id || '');
      }
      await fetchVoices();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove voice');
    }
  };

  const handlePreviewVoice = async () => {
    if (!selectedVoiceId) return;

    // If currently playing, stop
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setIsPlaying(false);
      return;
    }

    setIsPreviewLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: PREVIEW_TEXT,
          voiceId: selectedVoiceId,
          stability: stability,
          similarityBoost: similarityBoost,
          style: style,
          speed: speed,
          previewOnly: true // Don't save to S3, just return audio
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.audioUrl) {
        // Create audio element and play
        const audio = new Audio(data.audioUrl);
        audioRef.current = audio;

        audio.onended = () => {
          setIsPlaying(false);
          audioRef.current = null;
        };

        audio.onerror = () => {
          setError('Failed to play audio preview');
          setIsPlaying(false);
          audioRef.current = null;
        };

        await audio.play();
        setIsPlaying(true);
      } else {
        throw new Error('No audio URL returned from server');
      }
    } catch (err) {
      console.error('Failed to preview voice:', err);
      setError(err instanceof Error ? err.message : 'Failed to preview voice');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleSave = () => {
    onSave({ voiceId: selectedVoiceId, stability, similarityBoost, style, speed });
    onClose();
  };

  // Find the currently selected voice object for Autocomplete value
  const selectedVoiceObject = voices.find(v => v.voice_id === selectedVoiceId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Voice Settings</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {addVoiceSuccess && <Alert severity="success" sx={{ mb: 2 }}>{addVoiceSuccess}</Alert>}

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            id="voice-autocomplete"
            options={voices}
            getOptionLabel={(option) => option.name}
            value={selectedVoiceObject}
            onChange={(event, newValue) => {
              setSelectedVoiceId(newValue ? newValue.voice_id : '');
              // Stop any playing preview when voice changes
              if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
                setIsPlaying(false);
              }
            }}
            isOptionEqualToValue={(option, value) => option.voice_id === value.voice_id}
            loading={loadingVoices}
            disabled={loadingVoices || voices.length === 0}
            sx={{ flex: 1 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Voice"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingVoices ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <Tooltip title={isPlaying ? "Stop preview" : "Preview voice"}>
            <span>
              <IconButton
                onClick={handlePreviewVoice}
                disabled={!selectedVoiceId || isPreviewLoading}
                color={isPlaying ? "secondary" : "primary"}
                sx={{
                  border: '1px solid',
                  borderColor: isPlaying ? 'secondary.main' : 'primary.main',
                }}
              >
                {isPreviewLoading ? (
                  <CircularProgress size={24} />
                ) : isPlaying ? (
                  <StopIcon />
                ) : (
                  <PlayArrowIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            label="Add voice by ID"
            placeholder="Paste ElevenLabs voice ID"
            value={addVoiceId}
            onChange={(e) => {
              setAddVoiceId(e.target.value);
              setAddVoiceSuccess(null);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddVoice(); }}
            sx={{ flex: 1 }}
          />
          <Tooltip title="Add voice to approved list">
            <span>
              <IconButton
                onClick={handleAddVoice}
                disabled={!addVoiceId.trim() || addingVoice}
                color="primary"
                sx={{ border: '1px solid', borderColor: 'primary.main' }}
              >
                {addingVoice ? <CircularProgress size={24} /> : <AddIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* Approved voices — remove any from the allow-list (the dropdown above
            only shows approved voices). */}
        {voices.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Approved voices ({voices.length}) — click ✕ to remove
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', gap: 1 }}>
              {voices.map((v) => (
                <Chip
                  key={v.voice_id}
                  label={v.name || v.voice_id}
                  onDelete={() => handleRemoveVoice(v.voice_id, v.name)}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ mb: 3, px: 1 }}>
          <Typography gutterBottom>Stability</Typography>
          <Slider
            value={stability}
            onChange={(e, newValue) => setStability(newValue as number)}
            aria-labelledby="stability-slider"
            valueLabelDisplay="auto"
            step={null}
            marks={[
              { value: 0, label: 'Creative' },
              { value: 0.5, label: 'Natural' },
              { value: 1, label: 'Robust' }
            ]}
            min={0}
            max={1}
            sx={{
              '& .MuiSlider-markLabel[data-index="0"]': { transform: 'translateX(0%)' },
              '& .MuiSlider-markLabel[data-index="2"]': { transform: 'translateX(-100%)' },
            }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>Similarity Boost</Typography>
          <Slider
            value={similarityBoost}
            onChange={(e, newValue) => setSimilarityBoost(newValue as number)}
            aria-labelledby="similarity-boost-slider"
            valueLabelDisplay="auto"
            step={0.01}
            marks
            min={0}
            max={1}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>Style (Expressiveness)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Higher values = more dynamic and expressive delivery
          </Typography>
          <Slider
            value={style}
            onChange={(e, newValue) => setStyle(newValue as number)}
            aria-labelledby="style-slider"
            valueLabelDisplay="auto"
            step={0.05}
            marks={[
              { value: 0, label: '0' },
              { value: 0.5, label: '0.5' },
              { value: 1, label: '1' },
            ]}
            min={0}
            max={1}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>Speed</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            1.0 = normal, higher = faster/more energetic
          </Typography>
          <Slider
            value={speed}
            onChange={(e, newValue) => setSpeed(newValue as number)}
            aria-labelledby="speed-slider"
            valueLabelDisplay="auto"
            step={0.05}
            marks={[
              { value: 0.7, label: '0.7' },
              { value: 1.0, label: '1.0' },
              { value: 1.3, label: '1.3' },
            ]}
            min={0.7}
            max={1.3}
          />
        </Box>

        {/* Note: Speaker Boost is NOT supported by eleven_v3 model */}

      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loadingVoices}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 